import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatDate } from '../../utils/formatters.js';
import { countBy } from '../../utils/insightStats.js';

import { JobPostingCreateModal } from './JobPostingCreateModal.jsx';

const STATUS_ORDER = ['OPEN', 'PAUSED', 'CLOSED'];

function postingStatusColor(palette, status) {
  if (status === 'OPEN') return palette?.series?.[1];
  if (status === 'PAUSED') return palette?.series?.[2];
  return palette?.axis;
}

export function JobPostingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['hr', 'job-postings'],
    queryFn: () => hrApi.listJobPostings().then((r) => r.items || []),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => hrApi.setJobPostingStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hr', 'job-postings'] }),
  });

  const palette = useChartPalette();
  const stats = useMemo(() => {
    const rows = data || [];
    const counts = countBy(rows, (r) => r.status);
    const statuses = [
      ...STATUS_ORDER.filter((s) => counts[s]),
      ...Object.keys(counts).filter((s) => !STATUS_ORDER.includes(s)),
    ];
    return {
      total: rows.length,
      statuses,
      counts,
      open: counts.OPEN || 0,
      paused: counts.PAUSED || 0,
      closed: counts.CLOSED || 0,
    };
  }, [data]);

  const columns = [
    { key: 'title',  label: t('rec.postings.col.title') },
    { key: 'opensAt', label: t('rec.postings.col.opens'),  render: (r) => formatDate(r.opensAt) },
    { key: 'closesAt', label: t('rec.postings.col.closes'), render: (r) => formatDate(r.closesAt) },
    { key: 'status', label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    {
      key: 'actions',
      label: '',
      render: (r) => {
        const buttons = [];
        if (r.status === 'OPEN') {
          buttons.push(
            <Button key="pause" size="sm" variant="default" disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ id: r.id, status: 'PAUSED' })}>
              {t('rec.postings.action.pause')}
            </Button>,
            <Button key="close" size="sm" variant="danger" disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ id: r.id, status: 'CLOSED' })}>
              {t('rec.postings.action.close')}
            </Button>,
          );
        }
        if (r.status === 'PAUSED' || r.status === 'CLOSED') {
          buttons.push(
            <Button key="reopen" size="sm" variant="primary" disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ id: r.id, status: 'OPEN' })}>
              {t('rec.postings.action.reopen')}
            </Button>,
          );
        }
        return buttons.length ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{buttons}</div> : null;
      },
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('rec.postings.title')}</h1>
          <p className="muted">{t('rec.postings.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('rec.postings.action.new')}
          </Button>
        </div>
      </header>
      {stats.total > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={stats.statuses.map((s) => (s === '-' ? s : t(`status.${s}`)))}
          values={stats.statuses.map((s) => stats.counts[s])}
          colors={stats.statuses.map((s) => postingStatusColor(palette, s))}
          metrics={[
            { label: t('insight.count'), value: stats.total },
            { label: t('status.OPEN'), value: stats.open },
            { label: t('status.PAUSED'), value: stats.paused },
            { label: t('status.CLOSED'), value: stats.closed },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('rec.postings.empty')} />
      <JobPostingCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  );
}
